/*
 * Copyright (c) Funtip GbR 2017.
 * Simon Oldeboershuis, Perry Prust
 */

import { push } from '@lagunovsky/redux-react-router';
import { useQuery } from '@tanstack/react-query';
import * as R from 'ramda';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Badge } from 'reactstrap';
import { Action } from 'redux';
import UserLink from '../../links/UserLink';
import { withBorder } from '../../reactutils/withBorder';
import { withNavLocation } from '../../reactutils/withNavLocation';
import { PagingComponent } from '../../reactutils/withPagination';
import { SearchComponent } from '../../reactutils/withSearch';
import { FuntipTitle } from '../../reactutils/withTitle';
import { TournamentRoundAndTournamentSelectionParams } from '../../reactutils/withTournamentAndTournamentRoundSelection';
import { Highscore } from '../../services/betting';
import { BettingViewLink } from '../BettingApp/BettingView';
import { SubMenuOverallHihgscores } from '../BettingApp/SubMenuOverallHighscores';
import { usePathSegment } from '../SysAdminApp/CacheStatisticsComponent';
import { userIdSelector } from '../System/selectors';
import { getHighscoreApi2, getSystemConfig } from '../System/systemConfig';
import { TournamentSelectionComponent } from '../TournamentSelectionComponent';
import { TournamentRoundSelectionComponent2 } from '../TournamentSelectionComponent/TournamentRoundSelectionComponent';
import { noopBoolxAction } from '../actions';
import { LoadingIndicatorContent } from '../common/LoadingIndicatorSpinner';

export type HighscoreRequestParams = {
    trId: string;
    pos?: string;
};

export const getLastPageStart = (count: number, slotSize: number) => {
    return Math.floor(count / slotSize) * slotSize;
};

export const HIGHSCORE_SLOT_SIZE = 20;

export const Highscores: React.FC<{}> = () => {
    const userId = useSelector(userIdSelector);

    const [trId] = usePathSegment(3);
    const [offset] = usePathSegment(4) || 0;

    const [query, setQuery] = React.useState('');
    const [highscores, setHighscores] = React.useState<Highscore[]>();
    const [highscoreCount, setHighscoreCount] = React.useState(0);

    const highscoreQuery = useQuery({
        queryKey: ['searchHighscores', trId, offset, query],
        queryFn: () =>
            getHighscoreApi2().getHighscores({
                clientId: getSystemConfig().clientId,
                trId: Number(trId),
                startPos: Number(offset),
                slotSize: HIGHSCORE_SLOT_SIZE,
                q: query,
            }),
    });

    React.useEffect(() => {
        setHighscores(highscoreQuery.data);
    }, [highscoreQuery.data]);

    const highscoreCountQuery = useQuery({
        queryKey: ['highscoreCount', trId, query],
        queryFn: () =>
            getHighscoreApi2().getHighscoreCount({ clientId: getSystemConfig().clientId, trId: Number(trId), query }),
    });

    React.useEffect(() => {
        setHighscoreCount(highscoreCountQuery.data || 0);
    }, [highscoreCountQuery.data]);

    const dispatch = useDispatch();

    const pageUp = () => {
        if (Number(offset) + HIGHSCORE_SLOT_SIZE < highscoreCount) {
            dispatch(push(highscoresPath(trId, Number(offset) + HIGHSCORE_SLOT_SIZE)));
        }
        return noopBoolxAction(false);
    };

    const pageDown = () => {
        if (Number(offset) - HIGHSCORE_SLOT_SIZE >= 0) {
            dispatch(push(highscoresPath(trId, Number(offset) - HIGHSCORE_SLOT_SIZE)));
        }
        return noopBoolxAction(false);
    };

    const pageFirst = () => {
        dispatch(push(highscoresPath(trId, 0)));
        return noopBoolxAction(false);
    };

    const pageLast = () => {
        dispatch(push(highscoresPath(trId, getLastPageStart(highscoreCount, HIGHSCORE_SLOT_SIZE))));
        return noopBoolxAction(false);
    };

    const setSearchString = (s: string) => {
        setQuery(s);
        return pageFirst();
    };

    function renderHighscore(hs: Highscore) {
        const isMe = userId === String(hs.userId);
        return (
            <tr key={hs.id}>
                <td className="pe-0">
                    {isMe && (
                        <h5>
                            <Badge color="warning">{hs.rank}</Badge>
                        </h5>
                    )}
                    {!isMe && hs.rank}
                </td>
                <td className="ps-0">
                    <LoadingIndicatorContent isLoading={highscoreQuery.isFetching}>
                        <UserLink id={String(hs.userId)} name={hs.userName} showTrophies={true} />
                    </LoadingIndicatorContent>
                </td>
                <td>
                    <BettingViewLink trId={trId} userId={String(hs.userId)}>
                        {hs.points}
                    </BettingViewLink>
                </td>
                <td className="pe-1">{hs.betCount}</td>
            </tr>
        );
    }

    return (
        <>
            <FuntipTitle
                title="Einzelwertung"
                subTitle=""
                leftComponent={<TournamentSelectionComponent onTournamentChange={trParams.onTournamentIdChange} />}
                rightComponent={
                    <TournamentRoundSelectionComponent2
                        trId={trId}
                        type="highscores"
                        pathByTournamentRound={highscoresPath}
                    />
                }
            />
            <span className="mt-3">
                <SubMenuOverallHihgscores activeSubMenu="highscores" />
                <div className="d-flex align-items-center justify-content-between">
                    <SearchComponent
                        searchResultCountSelector={() => highscoreCount}
                        searchStringSelector={() => query}
                        onSearch={setSearchString}
                        placeholder="Tipper Suche"
                    />
                    <PagingComponent
                        onForward={pageUp}
                        onFastForward={pageLast}
                        onBackward={pageDown}
                        onFastBackward={pageFirst}
                        searchResultCount={() => highscoreCount}
                        searchResultCurrentPos={() => Number(offset)}
                        searchResultPageSize={() => HIGHSCORE_SLOT_SIZE}
                    />
                </div>
                <table className="mt-1 table">
                    <thead>
                        <tr>
                            <th className="ps-0 pe-0 border-top-0">PLATZ</th>
                            <th className="ps-0 border-top-0">NAME</th>
                            <th className="ps-0 pe-1 border-top-0">PUNKTE</th>
                            <th className="ps-0 pe-0 border-top-0">TIPPS</th>
                        </tr>
                    </thead>
                    <tbody>{highscores && highscores.map((hs: Highscore) => renderHighscore(hs))}</tbody>
                </table>
            </span>
        </>
    );
};

export const highscoresPath = (trId?: string, pos?: number) => {
    if (trId !== undefined) {
        return `/b/highscores/${trId}/${pos ? String(pos) : '0'}`;
    } else {
        return `/b/highscores`;
    }
};

const trParams: TournamentRoundAndTournamentSelectionParams = {
    type: 'highscores',
    pathByTournamentRound: () => highscoresPath,
    onTournamentRoundIdChange: (id: string) => push(highscoresPath(id)),
    onTournamentIdChange: (tId: string) => push(highscoresPath()),
};

export const HighscoreScreen = R.compose(withBorder(), withNavLocation('overall-highscore'))(Highscores);

export const ActionForwardComponent: React.FC<{ action: Action }> = (props) => {
    const dispatch = useDispatch();

    React.useEffect(() => {
        dispatch(props.action);
    }, [dispatch, props]);

    return null;
};
